import HelperMixin from '../../Shared/HelperMixin.vue';

export default {
    name: 'InventoryAlerts',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewInventoryAlertsUi()) {
            this.logout();
            return;
        }
        this.loadAlerts();
    },

    data() {
        return {
            nearExpiryDays: 60,
            loading: false,
            search: '',
            summary: {
                nearExpiryCount: 0,
                lowStockCount: 0
            },
            nearExpiry: [],
            lowStock: []
        };
    },

    computed: {
        totalAlerts() {
            return (this.summary.lowStockCount || 0) + (this.summary.nearExpiryCount || 0);
        },
        urgentExpiryCount() {
            return this.nearExpiry.filter((item) => {
                const days = this.daysUntil(item.expiryDate);
                return days !== null && days <= 7;
            }).length;
        },
        criticalLowStockCount() {
            return this.lowStock.filter((item) => this.getLowStockSeverity(item) === 'critical').length;
        },
        filteredLowStock() {
            return this.lowStock.filter((item) => this.matchesSearch([
                item.productName,
                item.batchNumber,
                item.serialNumber,
                item.stockingName,
                item.nationalCode,
                item.scientificName,
                item.totalQty,
                item.threshold
            ]));
        },
        filteredNearExpiry() {
            return this.nearExpiry.filter((item) => this.matchesSearch([
                item.productName,
                item.batchNumber,
                item.stockingName,
                item.serialNumber,
                item.nationalCode,
                item.scientificName,
                item.quantity,
                item.expiryDate ? this.moment(item.expiryDate) : ''
            ]));
        },
        statCards() {
            return [
                {
                    title: 'تشغيلات منخفضة المخزون',
                    value: this.summary.lowStockCount || 0,
                    tone: 'danger',
                    icon: 'ti ti-alert-triangle',
                    subtitle: 'تشغيلات تجاوزت حد إعادة الطلب أو الحد الأدنى'
                },
                {
                    title: 'تشغيلات قريبة الانتهاء',
                    value: this.summary.nearExpiryCount || 0,
                    tone: 'warning',
                    icon: 'ti ti-clock-hour-4',
                    subtitle: `ضمن نافذة المتابعة الحالية (${this.formatNumber(this.nearExpiryDays)} يوم)`
                },
                {
                    title: 'تنبيهات عاجلة',
                    value: this.urgentExpiryCount,
                    tone: 'primary',
                    icon: 'ti ti-siren',
                    subtitle: 'تشغيلات تنتهي خلال 7 أيام أو أقل'
                },
                {
                    title: 'حالات نقص حرجة',
                    value: this.criticalLowStockCount,
                    tone: 'success',
                    icon: 'ti ti-package-off',
                    subtitle: 'تشغيلات أوشكت على النفاد أو نفدت فعلياً'
                }
            ];
        }
    },

    methods: {
        loadAlerts() {
            const finishLoading = () => {
                this.loading = false;
                this.$blockUI.Stop();
            };

            this.loading = true;
            this.$blockUI.Start();
            this.$http.GetInventoryAlerts(this.nearExpiryDays)
                .then((response) => {
                    this.summary = response.data.summary || {};
                    this.nearExpiry = response.data.nearExpiry || [];
                    this.lowStock = response.data.lowStock || [];
                })
                .catch(() => {
                    this.summary = { nearExpiryCount: 0, lowStockCount: 0 };
                    this.nearExpiry = [];
                    this.lowStock = [];
                })
                .then(finishLoading);
        },
        matchesSearch(values) {
            const query = (this.search || '').trim().toLowerCase();
            if (!query) return true;

            return values.some((value) => String(value || '').toLowerCase().includes(query));
        },
        daysUntil(date) {
            if (!date) return null;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const target = new Date(date);
            target.setHours(0, 0, 0, 0);

            return Math.ceil((target - today) / 86400000);
        },
        formatDaysRemaining(date) {
            const days = this.daysUntil(date);
            if (days === null) return '-';
            if (days <= 0) return 'تنتهي اليوم';
            if (days === 1) return 'متبقي يوم واحد';
            return `متبقي ${this.formatNumber(days)} يوم`;
        },
        formatNumber(value) {
            return new Intl.NumberFormat('en-US').format(Number(value) || 0);
        },
        stockRatio(item) {
            const threshold = Number(item && item.threshold) || 0;
            const totalQty = Number(item && item.totalQty) || 0;

            if (threshold <= 0) return totalQty > 0 ? 100 : 0;

            return Math.max(0, Math.min(100, Math.round((totalQty / threshold) * 100)));
        },
        getLowStockSeverity(item) {
            const totalQty = Number(item && item.totalQty) || 0;
            const ratio = this.stockRatio(item);

            if (totalQty === 0 || ratio <= 30) return 'critical';
            if (ratio <= 70) return 'warning';
            return 'info';
        },
        getLowStockBadgeClass(item) {
            const severity = this.getLowStockSeverity(item);
            if (severity === 'critical') return 'bg-label-danger';
            if (severity === 'warning') return 'bg-label-warning';
            return 'bg-label-info';
        },
        getLowStockStatusText(item) {
            const totalQty = Number(item && item.totalQty) || 0;
            const severity = this.getLowStockSeverity(item);

            if (totalQty === 0) return 'نفد المخزون';
            if (severity === 'critical') return 'حرج';
            if (severity === 'warning') return 'منخفض';
            return 'قريب من الحد';
        },
        lowStockProgressClass(item) {
            const severity = this.getLowStockSeverity(item);
            if (severity === 'critical') return 'bg-danger';
            if (severity === 'warning') return 'bg-warning';
            return 'bg-info';
        },
        getExpirySeverity(item) {
            const days = this.daysUntil(item && item.expiryDate);

            if (days === null) return 'neutral';
            if (days <= 7) return 'critical';
            if (days <= 30) return 'warning';
            return 'info';
        },
        getExpiryBadgeClass(item) {
            const severity = this.getExpirySeverity(item);
            if (severity === 'critical') return 'bg-label-danger';
            if (severity === 'warning') return 'bg-label-warning';
            if (severity === 'info') return 'bg-label-info';
            return 'bg-label-secondary';
        },
        getExpiryStatusText(item) {
            const severity = this.getExpirySeverity(item);
            if (severity === 'critical') return 'عاجل';
            if (severity === 'warning') return 'قريب';
            if (severity === 'info') return 'ضمن النافذة';
            return 'غير محدد';
        }
    }
};
